import { type FilingPart, SendFilingDialog } from "@/components/accounting/send-filing-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { cn } from "@/lib/utils";
import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/accounting/vat` — VAT for a period.
 *
 * Hub builds both the return and the control statement and hands them
 * over as XML for the tax portal. **A person files them** — filing a tax
 * return on someone's behalf is a legal act, not an integration.
 */

const searchSchema = z.object({
  year: z.number().optional(),
  period: z.number().optional(),
  quarterly: z.boolean().optional(),
});

type Row = { rate: string; mode: string; base: string; vat: string };

type Summary = {
  output: Row[];
  input: Row[];
  outputBase: string;
  outputVat: string;
  inputBase: string;
  inputVat: string;
  taxDue: string;
};

type VatReturnRow = {
  id: string;
  form: "regular" | "corrective" | "additional";
  controlForm: "regular" | "corrective" | "follow_up" | null;
  status: "draft" | "ready" | "submitted";
  source: "hub" | "imported";
  taxDue: string;
  lastKnownTaxDue: string | null;
  discoveredOn: string | null;
  amendmentReasons: string | null;
  submittedAt: string | null;
  updatedAt: string;
  hasReturnXml: boolean;
  hasControlXml: boolean;
  dataBoxMessages: Array<{
    id: string;
    dmId: string;
    filingPart: "dphdp3" | "dphkh1" | null;
    messageStatus: number | null;
    sentAt: string | null;
    deliveredAt: string | null;
    acceptedAt: string | null;
  }>;
};

export const Route = createFileRoute("/_app/accounting/vat")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.year) params.set("year", String(deps.year));
    if (deps.period) params.set("period", String(deps.period));
    if (deps.quarterly) params.set("quarterly", "true");
    const qs = params.toString();

    try {
      return await api.get<{
        period: { year: number; period: number; quarterly: boolean };
        summary: Summary;
        returns: VatReturnRow[];
      }>(`/api/accounting/vat${qs ? `?${qs}` : ""}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: VatPage,
});

function money(amount: string): string {
  return formatNumber(Number(amount), undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function VatPage() {
  const { t } = useTranslation();
  const { period, summary, returns } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const [discoveredOn, setDiscoveredOn] = useState(() => new Date().toISOString().slice(0, 10));
  const additionalDraft = returns.find((r) => r.status !== "submitted" && r.form === "additional");
  const [reasons, setReasons] = useState(additionalDraft?.amendmentReasons ?? "");
  const savedReasons = additionalDraft?.amendmentReasons ?? "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: the text follows the selected period and its draft
  useEffect(() => {
    setReasons(savedReasons);
  }, [period.year, period.period, period.quarterly, additionalDraft?.id, savedReasons]);
  const [followUp, setFollowUp] = useState(true);
  const anyFiled = returns.some((r) => r.status === "submitted");
  const regularDraft = returns.some((r) => r.status !== "submitted" && r.form === "regular");

  const build = useApiMutation(
    (options: { form: "regular" | "additional" }) =>
      api.post("/api/accounting/vat/build", {
        ...period,
        form: options.form,
        ...(options.form === "additional"
          ? { discoveredOn, controlStatement: followUp, ...(reasons.trim() ? { reasons } : {}) }
          : {}),
      }),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: (err) => (err instanceof ApiError ? err.message : t("accVat.buildError")),
    },
  );

  const importFiled = useApiMutation(() => api.post("/api/accounting/vat/import-filed", {}), {
    onSuccess: () => router.invalidate(),
    errorMessage: () => t("accVat.importError"),
  });

  const markSubmitted = useApiMutation(
    (id: string) => api.post(`/api/accounting/vat/${id}/submitted`, {}),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accVat.markError") },
  );

  const taxDue = Number(summary.taxDue);
  const [sending, setSending] = useState<{ id: string; part: FilingPart } | null>(null);

  return (
    <div className="space-y-6 p-6">
      {sending && (
        <SendFilingDialog
          vatReturnId={sending.id}
          part={sending.part}
          onClose={() => setSending(null)}
          onSent={() => {
            setSending(null);
            void router.invalidate();
          }}
        />
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accVat.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("accVat.subtitle")}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label htmlFor="year" className="text-muted-foreground text-xs">
              {t("accVat.year")}
            </label>
            <input
              id="year"
              type="number"
              defaultValue={period.year}
              className="block h-9 w-24 rounded-md border border-input bg-background px-2 text-sm"
              onChange={(e) =>
                navigate({
                  to: "/accounting/vat",
                  search: { ...period, year: Number(e.target.value) },
                })
              }
            />
          </div>
          <div>
            <label htmlFor="period" className="text-muted-foreground text-xs">
              {period.quarterly ? t("accVat.quarter") : t("accVat.month")}
            </label>
            <input
              id="period"
              type="number"
              min={1}
              max={period.quarterly ? 4 : 12}
              defaultValue={period.period}
              className="block h-9 w-20 rounded-md border border-input bg-background px-2 text-sm"
              onChange={(e) =>
                navigate({
                  to: "/accounting/vat",
                  search: { ...period, period: Number(e.target.value) },
                })
              }
            />
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/accounting/vat",
                search: { ...period, quarterly: !period.quarterly, period: 1 },
              })
            }
          >
            {period.quarterly ? t("accVat.monthly") : t("accVat.quarterly")}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>{taxDue >= 0 ? t("accVat.vatDue") : t("accVat.vatRefund")}</CardTitle>
            <CardDescription>
              {t("accVat.balance", {
                output: money(summary.outputVat),
                input: money(summary.inputVat),
              })}
            </CardDescription>
          </div>
          <div className={`font-semibold text-2xl ${taxDue >= 0 ? "" : "text-emerald-600"}`}>
            {money(String(Math.abs(taxDue)))} Kč{/* i18n-ignore */}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {!anyFiled && (
              <Button disabled={build.pending} onClick={() => build.mutate({ form: "regular" })}>
                {build.pending
                  ? t("accVat.building")
                  : regularDraft
                    ? t("accVat.rebuild")
                    : t("accVat.buildReturn")}
              </Button>
            )}
            <Button
              variant="outline"
              disabled={importFiled.pending}
              onClick={() => importFiled.mutate()}
            >
              {importFiled.pending ? t("accVat.importing") : t("accVat.importFiled")}
            </Button>
          </div>
          {anyFiled && (
            <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
              <div>
                <label htmlFor="discovered" className="text-muted-foreground text-xs">
                  {t("accVat.reasonsFoundOn")}
                </label>
                <input
                  id="discovered"
                  type="date"
                  value={discoveredOn}
                  onChange={(e) => setDiscoveredOn(e.target.value)}
                  className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
                />
              </div>
              <label className="flex h-9 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={followUp}
                  onChange={(e) => setFollowUp(e.target.checked)}
                />
                {t("accVat.withFollowUp")}
              </label>
              <div className="w-full">
                <label htmlFor="reasons" className="text-muted-foreground text-xs">
                  {t("accVat.reasons")}
                </label>
                <textarea
                  id="reasons"
                  rows={3}
                  value={reasons}
                  onChange={(e) => setReasons(e.target.value)}
                  className="block w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                />
                <p className="text-muted-foreground text-xs">{t("accVat.reasonsHint")}</p>
              </div>
              <Button
                disabled={build.pending || !discoveredOn}
                onClick={() => build.mutate({ form: "additional" })}
              >
                {build.pending ? t("accVat.building") : t("accVat.buildAdditional")}
              </Button>
            </div>
          )}
          {returns.length > 0 && (
            <ReturnsTable
              returns={returns}
              onMark={(id) => markSubmitted.mutate(id)}
              marking={markSubmitted.pending}
              onSend={(id, part) => setSending({ id, part })}
            />
          )}
          {(build.error || markSubmitted.error || importFiled.error) && (
            <p className="text-destructive text-sm">
              {build.error ?? markSubmitted.error ?? importFiled.error}
            </p>
          )}
          <p className="text-muted-foreground text-xs">{t("accVat.footnote")}</p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <VatTable title={t("accVat.output")} rows={summary.output} total={summary.outputVat} />
        <VatTable title={t("accVat.input")} rows={summary.input} total={summary.inputVat} />
      </div>

      <YearFromDocuments
        year={period.year}
        month={period.quarterly ? null : period.period}
        onPick={(month) =>
          navigate({ to: "/accounting/vat", search: { year: period.year, period: month } })
        }
      />
      {!period.quarterly && <LinesFromDocuments year={period.year} month={period.period} />}
    </div>
  );
}

const MESSAGE_STATE: Record<number, string> = {
  1: "sent",
  2: "sent",
  4: "inBox",
  5: "deliveredFiction",
  6: "delivered",
  7: "delivered",
};

function FilingMessage({
  message,
}: {
  message: VatReturnRow["dataBoxMessages"][number];
}) {
  const { t } = useTranslation();
  const stateKey = message.messageStatus != null ? MESSAGE_STATE[message.messageStatus] : undefined;
  return (
    <div className="text-muted-foreground text-xs">
      {t("accVat.dataMessage")} <span className="font-mono">{message.dmId}</span>
      {message.messageStatus != null &&
        ` · ${
          stateKey
            ? t(`accVat.messageState.${stateKey}`)
            : t("accVat.messageState.unknown", { state: message.messageStatus })
        }`}
      {message.acceptedAt && (
        <>
          {" "}
          <LocalDate value={message.acceptedAt} />
        </>
      )}
    </div>
  );
}

function ReturnsTable({
  returns,
  onMark,
  marking,
  onSend,
}: {
  returns: VatReturnRow[];
  onMark: (id: string) => void;
  marking: boolean;
  onSend: (id: string, part: FilingPart) => void;
}) {
  const { t } = useTranslation();
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-muted-foreground">
        <tr>
          <th className="py-1 pr-4 font-medium">{t("accVat.returns.form")}</th>
          <th className="py-1 pr-4 font-medium">{t("accVat.returns.state")}</th>
          <th className="py-1 pr-4 text-right font-medium">{t("accVat.returns.taxDue")}</th>
          <th className="py-1 pr-4 text-right font-medium">{t("accVat.returns.difference")}</th>
          <th className="py-1 font-medium">{t("accVat.returns.files")}</th>
        </tr>
      </thead>
      <tbody>
        {returns.map((r) => {
          const difference =
            r.lastKnownTaxDue != null ? Number(r.taxDue) - Number(r.lastKnownTaxDue) : null;
          return (
            <tr key={r.id} className="border-t">
              <td className="py-1 pr-4">{t(`accVat.form.${r.form}`)}</td>
              <td className="py-1 pr-4">
                {r.status === "submitted" ? (
                  <Badge variant="success">
                    {t("accVat.returns.filed")}{" "}
                    {r.submittedAt && <LocalDate value={r.submittedAt} />}
                    {r.source === "imported" && ` ${t("accVat.returns.portal")}`}
                  </Badge>
                ) : (
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{t("accVat.returns.draft")}</Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={marking}
                      onClick={() => onMark(r.id)}
                    >
                      {t("accVat.returns.markFiled")}
                    </Button>
                  </span>
                )}
              </td>
              <td className="py-1 pr-4 text-right tabular-nums">{money(r.taxDue)}</td>
              <td className="py-1 pr-4 text-right tabular-nums">
                {difference != null ? money(String(difference)) : ""}
              </td>
              <td className="space-x-3 py-1">
                {r.hasReturnXml && (
                  <a
                    className="underline"
                    href={`/api/accounting/vat/${r.id}/xml`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    DPHDP3
                  </a>
                )}
                {r.hasControlXml && (
                  <a
                    className="underline"
                    href={`/api/accounting/vat/${r.id}/xml?kind=control`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    DPHKH1
                  </a>
                )}
                {r.source === "hub" &&
                  (
                    [
                      ["return", "dphdp3", r.hasReturnXml, "DPHDP3"],
                      ["control", "dphkh1", r.hasControlXml, "DPHKH1"],
                    ] as const
                  ).map(([part, filingPart, has, label]) => {
                    if (!has) return null;
                    const message = r.dataBoxMessages.find((m) => m.filingPart === filingPart);
                    if (message) return <FilingMessage key={part} message={message} />;
                    // Filed some other way: the data box would file it twice.
                    if (
                      r.status === "submitted" &&
                      !r.dataBoxMessages.some((m) => m.filingPart === "dphdp3")
                    ) {
                      return null;
                    }
                    return (
                      <div key={part}>
                        <Button variant="outline" size="sm" onClick={() => onSend(r.id, part)}>
                          {t("accVat.returns.sendByDataBox", { label })}
                        </Button>
                      </div>
                    );
                  })}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

type YearMonth = {
  month: number;
  outputVat: string;
  deduction: string;
  taxDue: string;
  documents: number;
  unplaced: number;
  notDeductible: number;
};

/** Each month's tax straight from the documents, whether posted or not. */
function YearFromDocuments({
  year,
  month,
  onPick,
}: {
  year: number;
  month: number | null;
  onPick: (month: number) => void;
}) {
  const { t } = useTranslation();
  const [months, setMonths] = useState<YearMonth[] | null>(null);
  useEffect(() => {
    setMonths(null);
    api
      .get<{ months: YearMonth[] }>(`/api/accounting/vat/lines/year?year=${year}`)
      .then((r) => setMonths(r.months))
      .catch(() => setMonths([]));
  }, [year]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("accVat.yearDocs.title", { year })}</CardTitle>
        <CardDescription>{t("accVat.yearDocs.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {months === null ? (
          <p className="text-muted-foreground text-sm">{t("accVat.yearDocs.computing")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-4 font-medium">{t("accVat.yearDocs.month")}</th>
                <th className="py-1 pr-4 text-right font-medium">
                  {t("accVat.yearDocs.outputVat")}
                </th>
                <th className="py-1 pr-4 text-right font-medium">
                  {t("accVat.yearDocs.deduction")}
                </th>
                <th className="py-1 pr-4 text-right font-medium">{t("accVat.yearDocs.taxDue")}</th>
                <th className="py-1 pr-4 text-right font-medium">
                  {t("accVat.yearDocs.documents")}
                </th>
                <th className="py-1 font-medium">{t("accVat.yearDocs.needsPerson")}</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month} className={cn("border-t", m.month === month && "bg-muted/40")}>
                  <td className="py-1 pr-4">
                    <button type="button" className="underline" onClick={() => onPick(m.month)}>
                      {m.month}
                    </button>
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(m.outputVat)}</td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(m.deduction)}</td>
                  <td className="py-1 pr-4 text-right font-medium tabular-nums">
                    {money(m.taxDue)}
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums">{m.documents}</td>
                  <td className="py-1">
                    {m.unplaced > 0 && (
                      <Badge variant="outline">
                        {t("accVat.yearDocs.toDecide", { count: m.unplaced })}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

type Contribution = {
  line: string;
  base: string;
  vat: string;
  document: {
    kind: string;
    id: string;
    number: string | null;
    partnerName: string | null;
    currency: string;
  };
  rule: string;
};

type Lines = {
  lines: Record<string, { base: string; vat: string }>;
  outputVat: string;
  deduction: string;
  taxDue: string;
  contributions: Contribution[];
  unplaced: Array<{
    id: string;
    number: string | null;
    partnerName: string | null;
    reason: string;
  }>;
  notDeductible: Contribution[];
};

/** One month's lines, each opening to the documents that make it up. */
function LinesFromDocuments({ year, month }: { year: number; month: number }) {
  const { t } = useTranslation();
  const [data, setData] = useState<Lines | null>(null);
  useEffect(() => {
    setData(null);
    api
      .get<Lines>(`/api/accounting/vat/lines?year=${year}&period=${month}`)
      .then(setData)
      .catch(() => setData(null));
  }, [year, month]);
  if (!data) return null;
  const keys = Object.keys(data.lines).sort((a, b) => Number(a) - Number(b));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("accVat.lines.title", { month, year })}</CardTitle>
        <CardDescription>
          {t("accVat.lines.description", {
            output: money(data.outputVat),
            deduction: money(data.deduction),
            taxDue: money(data.taxDue),
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {keys.map((key) => (
          <details key={key} className="rounded-md border p-2">
            <summary className="flex cursor-pointer flex-wrap items-center gap-2">
              <span className="w-10 font-mono">{t("accVat.lines.lineNo", { key })}</span>
              <span className="flex-1">{t(`accVat.lines.label.${key}`, { defaultValue: "" })}</span>
              <span className="tabular-nums">
                {t("accVat.lines.base", { value: money(data.lines[key]?.base ?? "0") })}
              </span>
              <span className="w-28 text-right tabular-nums">
                {t("accVat.lines.vat", { value: money(data.lines[key]?.vat ?? "0") })}
              </span>
            </summary>
            <ul className="mt-2 space-y-1 text-xs">
              {data.contributions
                .filter((c) => c.line === key)
                .map((c) => (
                  <li key={`${c.document.id}-${c.line}`} className="flex flex-wrap gap-2">
                    <span className="font-mono">{c.document.number ?? "—"}</span>
                    <span>{c.document.partnerName}</span>
                    <span className="tabular-nums">
                      {money(c.base)} / {money(c.vat)} CZK
                    </span>
                    <span className="text-muted-foreground">{c.rule}</span>
                  </li>
                ))}
            </ul>
          </details>
        ))}
        {data.notDeductible.length > 0 && (
          <details className="rounded-md border p-2">
            <summary className="cursor-pointer">
              {t("accVat.lines.notDeductible", { count: data.notDeductible.length })}
            </summary>
            <ul className="mt-2 space-y-1 text-xs">
              {data.notDeductible.map((c) => (
                <li key={c.document.id}>
                  <span className="font-mono">{c.document.number}</span> {c.document.partnerName} ·{" "}
                  {t("accVat.lines.vatCzk", { value: money(c.vat) })}
                </li>
              ))}
            </ul>
          </details>
        )}
        {data.unplaced.length > 0 && (
          <div className="rounded-md border border-amber-300 p-2">
            <p className="font-medium">
              {t("accVat.lines.unplaced", { count: data.unplaced.length })}
            </p>
            <ul className="mt-1 space-y-1 text-xs">
              {data.unplaced.map((u) => (
                <li key={u.id}>
                  <span className="font-mono">{u.number ?? "—"}</span> {u.partnerName}:{" "}
                  <span className="text-muted-foreground">{u.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VatTable({ title, rows, total }: { title: string; rows: Row[]; total: string }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("accVat.table.nothing")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-4 font-medium">{t("accVat.table.rate")}</th>
                <th className="py-1 pr-4 font-medium">{t("accVat.table.mode")}</th>
                <th className="py-1 pr-4 text-right font-medium">{t("accVat.table.base")}</th>
                <th className="py-1 text-right font-medium">{t("accVat.table.vat")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.rate}-${row.mode}`} className="border-t">
                  <td className="py-1 pr-4">{Number(row.rate).toFixed(0)} %</td>
                  <td className="py-1 pr-4 text-muted-foreground">
                    {t(`accVat.mode.${row.mode}`, { defaultValue: row.mode })}
                  </td>
                  <td className="py-1 pr-4 text-right tabular-nums">{money(row.base)}</td>
                  <td className="py-1 text-right tabular-nums">{money(row.vat)}</td>
                </tr>
              ))}
              <tr className="border-t-2 font-semibold">
                <td className="py-1 pr-4" colSpan={3}>
                  {t("accVat.table.total")}
                </td>
                <td className="py-1 text-right tabular-nums">{money(total)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
